/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo } from "react";

import clsx from "clsx";

import {
  useRouteMatch,
  NavLink,
  Switch,
  Route,
  Link,
  useLocation,
  Redirect,
} from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import {
  QUERY_CHAT_MESSAGING_AVAILABLE,
  QUERY_CLIENT,
} from "../../../config/graphql/query";
import { NEDAP_CLIENTS } from "../../../config/const/common";

import Tokens from "./Token/Tokens";
import Preferences from "./Preferences";

import SynguardFeature from "./Synguard/synguard";
import BrotherPrinter from "./BrotherPrinter/BrotherPrinter";
import SendGridClients from "./SendGrid/Clients";
import InformationForm from "./Information/Information";
import MessageTemplates from "./Message/MessageTemplates";
import NedapIntegration from "./Nedap/Integration";
import TeamsFeature from "./Teams/Integration";
import GoogleChatFeature from "./GoogleChat/Integration";
import ShellyFeature from "./Shelly/ShellyControllers";
import OutlookIntegrations from "./Outlook/Integrations";
import ActiveDirectoryIntegrations from "./ActiveDirectory/Integrations";
import GoogleWorkspaceIntegrations from "./GoogleWorkspace/integrations";
import TwilioClients, { useQueryTwilioClients } from "./Twilio/TwilioClients";
import MinewIntegration from "./Minew/Integration";
import DocumentScannerFeature from "./DocumentScanner/documentScanner";
import TuyaFeature from "./Tuya/Tuya";
import WebhookFeature from "./Webhook";
import VisitorPreregistration from "./VisitorPreregistration";
import DataRetentionSettings from "./DataRetention";
import ChatMessages from "./ChatMessages";

import useHasRole from "../../../lib/hooks/useHasRole";
import { useHasPlan } from "../../../components/PlanBlock";
import { useClientId } from "./hooks/useClient";
import { useBrotherPrinterTemplateOptions } from "../../../lib/hooks/useBrotherPrinterTemplateOptions";

export const ClientRoute = React.memo(() => {
  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const match = useRouteMatch();
  const location = useLocation();

  const { data: clientData } = useQuery<{ client: IClient }>(QUERY_CLIENT, {
    variables: { id: clientId },
    nextFetchPolicy: "network-only",
  });

  const brotherPrinterOptions = useBrotherPrinterTemplateOptions({ clientId });

  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);
  const isLowerThanClient = useHasRole(["TORO1"]);
  const isLowerThanEnterprise = useHasPlan(clientData?.client, [
    "free",
    "basic",
  ]);
  const hasNedap = NEDAP_CLIENTS.includes(clientData?.client?.code ?? "");

  // ADMIN only, and only for a client with Teams (own or shared) or Google Chat —
  // otherwise there is no chat message to configure.
  const { data: chatMessagingData } = useQuery<{
    chatMessagingAvailable: boolean;
  }>(QUERY_CHAT_MESSAGING_AVAILABLE, {
    skip: !clientId || !isAdmin,
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const hasChatMessages =
    isAdmin && !!chatMessagingData?.chatMessagingAvailable;

  const { data: twilioClientsData } = useQueryTwilioClients();

  const hasTwilioClient = useMemo(
    () =>
      twilioClientsData?.twilioClients &&
      Array.isArray(twilioClientsData?.twilioClients) &&
      twilioClientsData?.twilioClients.length > 0,
    [twilioClientsData?.twilioClients],
  );

  return (
    <>
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <NavLink exact className="nav-link" to={match.url}>
            {t("client:client.clientCreate.nav.information")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId,
            })}
            to={`${match.url}/preferences`}
          >
            {t("client:client.clientCreate.nav.preferences")}
          </NavLink>
        </li>
        {isAdmin && (
          <li className="nav-item">
            <NavLink
              className={clsx("nav-link", {
                disabled: !clientId,
              })}
              to={`${match.url}/token`}
            >
              {t("client:client.clientCreate.nav.tokens")}
            </NavLink>
          </li>
        )}
        {(isAdmin || isClientAdmin) && (
          <li className="nav-item">
            <NavLink
              className={clsx("nav-link", {
                disabled:
                  !clientId || isLowerThanEnterprise || isLowerThanClient,
              })}
              to={`${match.url}/twilio${!isAdmin ? "-message-templates" : ""}`}
              isActive={() => {
                return location.pathname.includes("twilio");
              }}
            >
              {t("client:client.clientCreate.nav.twilio") +
                (!isAdmin
                  ? ` ${t("client:client.sendgrid.nav.message-templates")}`
                  : "")}
            </NavLink>
          </li>
        )}
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/outlook`}
          >
            {t("client:client.clientCreate.nav.integrations.outlook")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/activedirectory`}
          >
            {t("client:client.clientCreate.nav.activeDirectory")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/googleWorkspace`}
          >
            {t("client:client.clientCreate.nav.googleWorkspace")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/minew`}
          >
            {t("client:client.clientCreate.nav.minew")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/sendgrid`}
            isActive={() => {
              return location.pathname.includes("sendgrid");
            }}
          >
            {t("client:client.clientCreate.nav.sendgrid")}
          </NavLink>
        </li>
        {!isLowerThanEnterprise && !isLowerThanClient && hasNedap && (
          <li className="nav-item">
            <NavLink
              className={clsx("nav-link", {
                disabled:
                  !clientId || isLowerThanEnterprise || isLowerThanClient,
              })}
              to={`${match.url}/nedap`}
              isActive={() => !!location.pathname.includes("nedap")}
            >
              {t("client:client.clientCreate.nav.nedap")}
            </NavLink>
          </li>
        )}
        {(isAdmin || brotherPrinterOptions?.enabled) &&
          !isLowerThanEnterprise &&
          !isLowerThanClient && (
            <li className="nav-item">
              <NavLink
                className={clsx("nav-link", {
                  disabled:
                    !clientId || isLowerThanEnterprise || isLowerThanClient,
                })}
                to={`${match.url}/brother-printer`}
                isActive={() => !!location.pathname.includes("brother-printer")}
              >
                {t("client:client.clientCreate.nav.brotherPrinter")}
              </NavLink>
            </li>
          )}
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/teams`}
          >
            {t("client:client.clientCreate.nav.teams")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/googlechat`}
          >
            {t("client:client.clientCreate.nav.googleChat")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/synguard`}
          >
            {t("client:client.clientCreate.nav.synguard")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/shelly`}
            isActive={() => {
              return location.pathname.includes("shelly");
            }}
          >
            {t("client:client.clientCreate.nav.shelly")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/tuya`}
            isActive={() => {
              return location.pathname.includes("tuya");
            }}
          >
            {t("client:client.clientCreate.nav.tuya")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/integrations/documentScanner`}
          >
            {t("client:client.clientCreate.nav.documentScanner")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/webhook`}
          >
            {t("client:client.clientCreate.nav.webhook")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/visitor-preregistration`}
            isActive={() =>
              location.pathname.includes("visitor-preregistration")
            }
          >
            {t("client:client.clientCreate.nav.visitorPreregistration")}
          </NavLink>
        </li>
        <li className="nav-item">
          <NavLink
            className={clsx("nav-link", {
              disabled: !clientId || isLowerThanEnterprise || isLowerThanClient,
            })}
            to={`${match.url}/data-retention`}
          >
            {t("client:client.clientCreate.nav.dataRetention")}
          </NavLink>
        </li>
        {hasChatMessages && (
          <li className="nav-item">
            <NavLink className="nav-link" to={`${match.url}/chat-messages`}>
              {t("client:client.clientCreate.nav.chatMessages")}
            </NavLink>
          </li>
        )}
      </ul>

      <div className="tab-content">
        {hasTwilioClient && (
          <Route
            path={[
              `${match.path}/twilio`,
              `${match.path}/twilio-message-templates`,
            ]}
          >
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <NavLink
                  exact
                  className={clsx("nav-link", {
                    disabled: !isAdmin,
                  })}
                  to={`${match.url}/twilio`}
                >
                  {t("client:client.sendgrid.nav.apikeys")}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  className="nav-link"
                  to={`${match.url}/twilio-message-templates`}
                >
                  {t("client:client.sendgrid.nav.message-templates")}
                </NavLink>
              </li>
            </ul>
          </Route>
        )}

        <Switch>
          <Route exact path={match.path}>
            <div className="tab-pane active" role="tabpanel">
              <InformationForm />
            </div>
          </Route>

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/outlook`}>
              <div className="tab-pane active" role="tabpanel">
                <OutlookIntegrations />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/activedirectory`}>
              <div className="tab-pane active" role="tabpanel">
                <ActiveDirectoryIntegrations />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/googleWorkspace`}>
              <div className="tab-pane active" role="tabpanel">
                <GoogleWorkspaceIntegrations />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/minew`}>
              <div className="tab-pane active" role="tabpanel">
                <MinewIntegration />
              </div>
            </Route>
          )}

          <Route path={`${match.path}/preferences`}>
            <div className="tab-pane active" role="tabpanel">
              <Preferences />
            </div>
          </Route>

          {!isLowerThanEnterprise && !isLowerThanClient && hasNedap && (
            <Route path={`${match.path}/nedap`}>
              <div className="tab-pane active" role="tabpanel">
                <NedapIntegration />
              </div>
            </Route>
          )}

          {(isAdmin || brotherPrinterOptions?.enabled) &&
            !isLowerThanEnterprise &&
            !isLowerThanClient && (
              <Route path={`${match.path}/brother-printer`}>
                <div className="tab-pane active" role="tabpanel">
                  <BrotherPrinter />
                </div>
              </Route>
            )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/teams`}>
              <div className="tab-pane active" role="tabpanel">
                <TeamsFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/googlechat`}>
              <div className="tab-pane active" role="tabpanel">
                <GoogleChatFeature />
              </div>
            </Route>
          )}

          {hasChatMessages && (
            <Route exact path={`${match.path}/chat-messages`}>
              <div className="tab-pane active" role="tabpanel">
                <ChatMessages />
              </div>
            </Route>
          )}

          {isAdmin && (
            <Route exact path={`${match.path}/token`}>
              <div className="tab-pane active" role="tabpanel">
                <Tokens />
              </div>
            </Route>
          )}

          {isAdmin && !isLowerThanEnterprise && (
            <Route exact path={`${match.path}/twilio`}>
              <div className="tab-pane active" role="tabpanel">
                <TwilioClients />
              </div>
            </Route>
          )}
          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/sendgrid`}>
              <div className="tab-pane active" role="tabpanel">
                <SendGridClients />
              </div>
            </Route>
          )}
          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route path={`${match.path}/twilio-message-templates`}>
              <div className="tab-pane active" role="tabpanel">
                <MessageTemplates />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/synguard`}>
              <div className="tab-pane active" role="tabpanel">
                <SynguardFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/shelly`}>
              <div className="tab-pane active" role="tabpanel">
                <ShellyFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/integrations/documentScanner`}>
              <div className="tab-pane active" role="tabpanel">
                <DocumentScannerFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route exact path={`${match.path}/tuya`}>
              <div className="tab-pane active" role="tabpanel">
                <TuyaFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route path={`${match.path}/webhook`}>
              <div className="tab-pane active" role="tabpanel">
                <WebhookFeature />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route path={`${match.path}/visitor-preregistration`}>
              <div className="tab-pane active" role="tabpanel">
                <VisitorPreregistration />
              </div>
            </Route>
          )}

          {!isLowerThanEnterprise && !isLowerThanClient && (
            <Route path={`${match.path}/data-retention`}>
              <div className="tab-pane active" role="tabpanel">
                <DataRetentionSettings />
              </div>
            </Route>
          )}

          <Redirect
            from={`${match.path}/integration`}
            to={`${match.path}/integrations/outlook`}
          />
        </Switch>
      </div>
    </>
  );
});

const ClientPage = () => {
  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery<{ client: IClient }>(QUERY_CLIENT, {
    variables: { id: clientId },
    nextFetchPolicy: "network-only",
    skip: !clientId,
  });

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          {isAdmin && (
            <li className="breadcrumb-item">
              <Link to="/clients">
                {t("client:client.clientCreate.nav.clients")}
              </Link>
            </li>
          )}
          <li className="breadcrumb-item active" aria-current="page">
            {data?.client?.title ?? "Client"}
          </li>
        </ol>
      </nav>
      <ClientRoute />
    </div>
  );
};

export const ClientCreatePage = () => {
  const { t } = useTranslation(["client", "common"]);

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/clients">
              {t("client:client.clientCreate.nav.clients")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("client:client.clientCreate.nav.client")}
          </li>
        </ol>
      </nav>
      <InformationForm />
    </div>
  );
};

export default ClientPage;
